import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, Hotel, DollarSign, Calendar, Users, BedDouble, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';

export default function WalkInPage() {
  const [searchParams] = useSearchParams();
  const hotelId = searchParams.get('hotel');
  
  const [step, setStep] = useState(1);
  const [hotel, setHotel] = useState(null);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  
  const [form, setForm] = useState({
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    check_in: new Date().toISOString().split('T')[0],
    check_out: '',
    guests: 2,
    payment_method: 'pay_at_property'
  });

  useEffect(() => {
    if (hotelId) {
      loadHotelAndRooms();
    }
  }, [hotelId]);

  const loadHotelAndRooms = async () => {
    try {
      const backendUrl = BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/hotels/${hotelId}/public`);
      const data = await response.json();
      setHotel(data.hotel);
      setAvailableRooms(data.rooms);
    } catch (error) {
      console.error('Error loading hotel:', error);
      toast.error('Failed to load hotel information');
    }
  };

  const handleSubmitInfo = async () => {
    if (!form.guest_name || !form.guest_email || !form.check_out) {
      toast.error('Please fill all required fields');
      return;
    }

    setStep(2);
  };

  const handleSelectRoom = (room) => {
    setSelectedRoom(room);
  };

  const handleConfirmWalkIn = async () => {
    if (!selectedRoom) {
      toast.error('Please select a room');
      return;
    }

    setLoading(true);
    try {
      const backendUrl = BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/checkin/walk-in/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hotel_id: hotelId,
          room_id: selectedRoom.id,
          ...form
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setConfirmationCode(data.confirmation_code);
        setStep(3);
        toast.success(data.message);
      } else {
        toast.error('Failed to complete check-in');
      }
    } catch (error) {
      console.error('Walk-in error:', error);
      toast.error('Failed to complete check-in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="walkin-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold heading-font mb-2">
            {hotel ? hotel.name : 'Walk-In Booking'}
          </h1>
          <p className="text-muted-foreground">
            {hotel?.city && `${hotel.city}, ${hotel.country} • `}
            Welcome! Let's get you checked in
          </p>
        </div>

        {/* Step Indicators */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                s === step ? 'bg-primary text-primary-foreground' :
                s < step ? 'bg-green-500 text-white' :
                'bg-muted text-muted-foreground'
              }`}>
                {s < step ? '✓' : s}
              </div>
              {s < 3 && <div className={`w-12 h-1 ${
                s < step ? 'bg-green-500' : 'bg-muted'
              }`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Guest Information */}
        {step === 1 && (
          <Card data-testid="walkin-step1-card">
            <CardHeader>
              <CardTitle className="heading-font">Your Information</CardTitle>
              <CardDescription>Please provide your details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    placeholder="John Doe"
                    value={form.guest_name}
                    onChange={(e) => setForm({...form, guest_name: e.target.value})}
                    data-testid="walkin-name-input"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    placeholder="john@example.com"
                    value={form.guest_email}
                    onChange={(e) => setForm({...form, guest_email: e.target.value})}
                    data-testid="walkin-email-input"
                  />
                </div>
              </div>

              <div>
                <Label>Phone Number</Label>
                <Input
                  type="tel"
                  placeholder="+1 234 567 8900"
                  value={form.guest_phone}
                  onChange={(e) => setForm({...form, guest_phone: e.target.value})}
                  data-testid="walkin-phone-input"
                />
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <Label>Check-in *</Label>
                  <Input
                    type="date"
                    value={form.check_in}
                    onChange={(e) => setForm({...form, check_in: e.target.value})}
                    data-testid="walkin-checkin-input"
                  />
                </div>
                <div>
                  <Label>Check-out *</Label>
                  <Input
                    type="date"
                    value={form.check_out}
                    min={form.check_in}
                    onChange={(e) => setForm({...form, check_out: e.target.value})}
                    data-testid="walkin-checkout-input"
                  />
                </div>
                <div>
                  <Label>Guests</Label>
                  <Input
                    type="number"
                    min="1"
                    value={form.guests}
                    onChange={(e) => setForm({...form, guests: parseInt(e.target.value)})}
                    data-testid="walkin-guests-input"
                  />
                </div>
              </div>

              <Button
                onClick={handleSubmitInfo}
                className="w-full h-12"
                data-testid="walkin-next-button"
              >
                Continue to Room Selection
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Room Selection */}
        {step === 2 && (
          <div className="space-y-4" data-testid="walkin-step2-rooms">
            <Button
              variant="ghost"
              onClick={() => setStep(1)}
              className="mb-4"
            >
              ← Back
            </Button>
            
            <h2 className="text-xl font-semibold heading-font">Select Your Room</h2>
            
            <div className="grid gap-4">
              {availableRooms.map((room) => (
                <Card
                  key={room.id}
                  className={`cursor-pointer transition-all ${
                    selectedRoom?.id === room.id
                      ? 'border-primary ring-2 ring-primary/20'
                      : 'hover:border-primary/50'
                  }`}
                  onClick={() => handleSelectRoom(room)}
                  data-testid="walkin-room-card"
                >
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-lg">{room.name}</h3>
                        <p className="text-sm text-muted-foreground">{room.description}</p>
                      </div>
                      {selectedRoom?.id === room.id && (
                        <CheckCircle className="w-5 h-5 text-primary" />
                      )}
                    </div>

                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Users className="w-4 h-4" />
                        Up to {room.max_guests} guests
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold mono">${room.price_per_night}</p>
                        <p className="text-xs text-muted-foreground">per night</p>
                      </div>
                    </div>

                    {room.amenities && room.amenities.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {room.amenities.slice(0, 4).map((amenity, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {amenity}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {selectedRoom && (
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-muted-foreground">Payment Method</span>
                    <RadioGroup value={form.payment_method} onValueChange={(v) => setForm({...form, payment_method: v})}>
                      <div className="flex gap-4">
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="pay_at_property" id="pay-property" />
                          <Label htmlFor="pay-property" className="font-normal cursor-pointer">Pay at Property</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="crypto" id="pay-crypto" />
                          <Label htmlFor="pay-crypto" className="font-normal cursor-pointer">Crypto</Label>
                        </div>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="flex items-center justify-between text-lg font-semibold">
                    <span>Total</span>
                    <span className="mono">${selectedRoom.price_per_night}</span>
                  </div>

                  <Button
                    onClick={handleConfirmWalkIn}
                    disabled={loading}
                    className="w-full h-12 mt-4"
                    data-testid="walkin-confirm-button"
                  >
                    {loading ? 'Processing...' : 'Confirm & Check In'}
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <Card className="border-green-200" data-testid="walkin-success-card">
            <CardHeader>
              <div className="flex items-center justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
              </div>
              <CardTitle className="text-center heading-font text-2xl">Welcome!</CardTitle>
              <CardDescription className="text-center">
                You're all checked in, {form.guest_name}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <AlertDescription className="text-green-900">
                  Walk-in booking created and checked in successfully!
                </AlertDescription>
              </Alert>

              <div className="bg-muted rounded-xl p-4 space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground">Confirmation Code</p>
                  <p className="text-2xl font-bold mono">{confirmationCode}</p>
                </div>
                
                <div>
                  <p className="text-sm text-muted-foreground">Room</p>
                  <p className="font-semibold">{selectedRoom?.name}</p>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Check-out</p>
                    <p className="font-semibold">{form.check_out}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Guests</p>
                    <p className="font-semibold">{form.guests}</p>
                  </div>
                </div>
              </div>

              <p className="text-sm text-muted-foreground text-center">
                Please proceed to the front desk to complete payment and receive your room key.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
